import { forwardRef, type ButtonHTMLAttributes } from "react";

import { cn } from "@/lib/utils";

type Variant = "primary" | "secondary" | "ghost" | "danger" | "success";
type Size = "sm" | "md";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  isLoading?: boolean;
}

const variantClasses: Record<Variant, string> = {
  primary:
    "border border-transparent bg-brand-600 text-white hover:bg-brand-700 disabled:bg-brand-600/60",
  secondary:
    "bg-white text-slate-900 border border-slate-300 hover:bg-slate-100 disabled:bg-slate-50",
  ghost:
    "border border-transparent bg-transparent text-slate-700 hover:bg-slate-100 disabled:text-slate-400",
  danger: "border border-transparent bg-red-600 text-white hover:bg-red-700 disabled:bg-red-600/60",
  success: "border border-transparent bg-green-600 text-white hover:bg-green-700 disabled:bg-green-600/60",
};

const sizeClasses: Record<Size, string> = {
  sm: "h-8 px-3 text-sm",
  md: "h-10 px-4 text-sm",
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = "primary", size = "md", isLoading, disabled, children, type = "button", ...rest }, ref) => {
    return (
      <button
        ref={ref}
        type={type}
        className={cn(
          "hub-pressable inline-flex items-center justify-center gap-2 rounded-md font-medium transition-[color,background-color,border-color,box-shadow,transform] duration-100 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed touch-manipulation motion-reduce:transition-none",
          variantClasses[variant],
          sizeClasses[size],
          className,
        )}
        disabled={disabled || isLoading}
        aria-busy={isLoading || undefined}
        {...rest}
      >
        <span className="inline-grid">
          <span
            className={cn(
              "col-start-1 row-start-1 inline-flex items-center justify-center gap-2",
              isLoading && "invisible",
            )}
          >
            {children}
          </span>
          {isLoading ? (
            <span className="col-start-1 row-start-1 flex items-center justify-center" aria-hidden>
              <span className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" />
            </span>
          ) : null}
        </span>
      </button>
    );
  },
);
Button.displayName = "Button";
